import React, { useEffect, useState, useMemo } from "react";
import { useAuth } from "../lib/auth";
import api, { formatApiErrorDetail } from "../lib/api";
import { Link } from "react-router-dom";
import {
  LogOut, ShieldCheck, ShieldAlert, CheckCircle2, AlertTriangle, Layers,
  FileText, Calendar, Download, MapPin, Building, Clock, Send, X, CalendarClock,
} from "lucide-react";
import { fmtDateTime } from "../lib/datetime";

const STATUS_COLOUR = {
  "DRA Signed":  { bg: "#e0e7ff", color: "#3730a3", border: "#6366f1" },
  Erected:       { bg: "#dbeafe", color: "#1e40af", border: "#3b82f6" },
  Standing:      { bg: "#d1fae5", color: "#065f46", border: "#059669" },
};

function StatusPill({ status }) {
  const c = STATUS_COLOUR[status] || STATUS_COLOUR.Standing;
  return (
    <span className="mono" style={{
      display: "inline-block", padding: "2px 8px", fontSize: 10, fontWeight: 700,
      letterSpacing: "0.04em", background: c.bg, color: c.color,
      border: `1px solid ${c.border}`, borderRadius: 2,
    }}>{status.toUpperCase()}</span>
  );
}

function McCainShell({ branding, viewer, children, onLogout }) {
  // Now mounted inside AppShell (which provides sidebar + sign-out). Render a slim page
  // header strip for context only — no duplicate logout button, no full-page chrome.
  return (
    <div data-testid="mccain-shell" style={{ padding: "24px clamp(16px, 4vw, 32px) 60px" }}>
      <header style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", gap: 14, flexWrap: "wrap", marginBottom: 18, paddingBottom: 14, borderBottom: "1px solid #e2e8f0" }} data-testid="mccain-header">
        <div>
          <div className="mono" style={{ fontSize: 10, letterSpacing: "0.14em", color: "#ff5a00", fontWeight: 600 }}>NEWELL · McCAIN WHITTLESEY</div>
          <h1 className="font-chivo" style={{ fontSize: 22, fontWeight: 900, lineHeight: 1.1, margin: "4px 0 0", color: "#0f172a" }}>McCain Operations Dashboard</h1>
        </div>
        <div style={{ textAlign: "right" }}>
          <div className="mono" style={{ fontSize: 10, letterSpacing: "0.08em", color: "#64748b" }}>{viewer?.role?.toUpperCase()}</div>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>{viewer?.name || "—"}</div>
        </div>
      </header>
      {children}
    </div>
  );
}

function KPI({ label, value, accent, testid }) {
  return (
    <div className="card" data-testid={testid} style={{ padding: "14px 16px", borderTop: `3px solid ${accent || "#cbd5e1"}` }}>
      <div className="mono" style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.08em", color: "#475569" }}>{label}</div>
      <div className="font-chivo" style={{ fontSize: 30, fontWeight: 900, color: "#0f172a", lineHeight: 1, marginTop: 4 }}>{value}</div>
    </div>
  );
}

export default function MCCain() {
  const { user, logout } = useAuth();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [tab, setTab] = useState("structures");
  const [requestModalOpen, setRequestModalOpen] = useState(false);
  const [requestSuccess, setRequestSuccess] = useState("");
  const [rotaDiary, setRotaDiary] = useState(null);
  const isManagement = user?.role === "mccain_management";
  const isSupervisor = user?.role === "mccain_supervisor" || user?.role === "mccain" || user?.role === "mccain_external";
  const isMccainAny = isManagement || isSupervisor;
  const canRequestCallout = isMccainAny || ["admin", "contracts_manager"].includes(user?.role);

  useEffect(() => {
    api.get("/mccain/dashboard")
      .then((r) => setData(r.data))
      .catch((e) => setErr(e.response?.data?.detail || e.message));
  }, []);

  useEffect(() => {
    if (tab === "rota" && (isManagement || ["admin", "contracts_manager"].includes(user?.role)) && rotaDiary === null) {
      api.get("/mccain/rota-diary").then((r) => setRotaDiary(r.data)).catch((e) => setRotaDiary([]));
    }
  }, [tab, isManagement, user?.role, rotaDiary]);

  const sorted = useMemo(() => {
    if (!data?.structures) return [];
    return [...data.structures].sort((a, b) => {
      // Standing-and-overdue first, then by next due
      if (a.overdue && !b.overdue) return -1;
      if (!a.overdue && b.overdue) return 1;
      const ad = a.days_to_next_inspection ?? 9999;
      const bd = b.days_to_next_inspection ?? 9999;
      return ad - bd;
    });
  }, [data]);

  const downloadRAMSPdf = (id) => {
    const url = `${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "")}/api/rams/${id}/pdf`;
    window.open(url, "_blank");
  };
  const downloadHandoverPdf = (id) => {
    const url = `${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "")}/api/handovers/${id}/pdf`;
    window.open(url, "_blank");
  };

  if (err) {
    return (
      <McCainShell branding={{}} viewer={user} onLogout={logout}>
        <div className="badge badge-red" data-testid="mccain-error" style={{ padding: "10px 14px" }}>{err}</div>
      </McCainShell>
    );
  }

  if (!data) {
    return (
      <McCainShell branding={{}} viewer={user} onLogout={logout}>
        <div className="mono" data-testid="mccain-loading" style={{ color: "#64748b", padding: 30 }}>Loading McCain dashboard…</div>
      </McCainShell>
    );
  }

  const k = data.headline;

  return (
    <McCainShell branding={data.branding || {}} viewer={data.viewer || user} onLogout={logout}>
      {/* Hero stats */}
      <section data-testid="mccain-headline" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12, marginBottom: 24 }}>
        <KPI label="Structures standing" value={k.structures_standing} accent="#059669" testid="kpi-standing" />
        <KPI label="Inspections overdue" value={k.inspections_overdue} accent={k.inspections_overdue > 0 ? "#dc2626" : "#cbd5e1"} testid="kpi-overdue" />
        <KPI label="Due within 7 days" value={k.due_within_7d} accent="#f59e0b" testid="kpi-due7" />
        <KPI label="RAMS signed (7d)" value={k.rams_signed_this_week} accent="#6366f1" testid="kpi-rams-week" />
        <KPI label="Handovers (7d)" value={k.handovers_this_week} accent="#3b82f6" testid="kpi-handovers-week" />
        <KPI label="Inspections (7d)" value={k.inspections_this_week} accent="#059669" testid="kpi-insp-week" />
      </section>

      {/* Request callout (callout-initiation front door — feeds NRS) */}
      {canRequestCallout ? (
        <div className="card" data-testid="mccain-request-callout-cta" style={{ padding: 16, marginBottom: 20, background: "#fff7ed", borderLeft: "4px solid #ff5a00", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
          <div>
            <div className="mono" style={{ fontSize: 11, color: "#9a3412", letterSpacing: "0.1em", marginBottom: 4 }}>OUT-OF-HOURS · 60-MIN SLA</div>
            <div style={{ fontSize: 14, color: "#0f172a" }}><strong>Need a scaffolder out of hours?</strong> Submit a request — the on-call team is alerted immediately and you&apos;ll receive a confirmation with the responder&apos;s ETA.</div>
          </div>
          <button data-testid="mccain-request-callout-btn" className="btn-primary" style={{ padding: "12px 18px", fontWeight: 700 }} onClick={() => setRequestModalOpen(true)}>
            <AlertTriangle size={14} style={{ verticalAlign: -2, marginRight: 8 }} />Request callout
          </button>
        </div>
      ) : null}

      {requestSuccess ? (
        <div data-testid="mccain-callout-success" className="card" style={{ padding: 12, marginBottom: 14, background: "#ecfdf5", color: "#065f46", borderLeft: "4px solid #059669", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span><CheckCircle2 size={14} style={{ verticalAlign: "middle", marginRight: 6 }} />{requestSuccess}</span>
          <button className="btn-ghost" onClick={() => setRequestSuccess("")}><X size={12} /></button>
        </div>
      ) : null}


      {/* Tabs */}
      <div data-testid="mccain-tabs" style={{ display: "flex", borderBottom: "1px solid #e2e8f0", marginBottom: 16, gap: 4, overflowX: "auto" }}>
        <TabBtn id="structures" tab={tab} setTab={setTab} icon={Building} label={`Structures (${data.structures.length})`} />
        {isManagement || ["admin", "contracts_manager"].includes(user?.role) ? (
          <TabBtn id="rams" tab={tab} setTab={setTab} icon={ShieldAlert} label={`RAMS — DRA signed (${data.rams.length})`} />
        ) : null}
        <TabBtn id="handovers" tab={tab} setTab={setTab} icon={FileText} label={`Handovers (${data.handovers.length})`} />
        {isManagement || ["admin", "contracts_manager"].includes(user?.role) ? (
          <TabBtn id="rota" tab={tab} setTab={setTab} icon={CalendarClock} label="On-call rota" />
        ) : null}
      </div>

      {/* Tab content */}
      {tab === "structures" ? (
        <div className="card" style={{ padding: 0 }} data-testid="mccain-structures-table">
          <table>
            <thead>
              <tr>
                <th>Description</th>
                <th>Status</th>
                <th>Height</th>
                <th>Last inspection</th>
                <th>Next due</th>
                <th>Location</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((s) => (
                <tr key={s.id} data-testid={`mccain-struct-${s.id}`}>
                  <td>
                    <div style={{ fontWeight: 600 }}>{s.description}</div>
                    <div className="mono" style={{ fontSize: 11, color: "#94a3b8" }}>{s.source}</div>
                  </td>
                  <td><StatusPill status={s.status} /></td>
                  <td className="mono" style={{ fontSize: 12 }}>{s.height_band || "—"}</td>
                  <td className="mono" style={{ fontSize: 11, color: "#64748b" }}>{s.last_inspection_at ? s.last_inspection_at.slice(0, 10) : "—"}</td>
                  <td>
                    {s.next_inspection_due ? (
                      <div>
                        <div className="mono" style={{ fontSize: 11 }}>{s.next_inspection_due.slice(0, 10)}</div>
                        {s.overdue ? (
                          <span className="badge badge-red" style={{ fontSize: 10 }}><AlertTriangle size={10} /> {Math.abs(s.days_to_next_inspection)}d overdue</span>
                        ) : s.days_to_next_inspection !== null && s.days_to_next_inspection <= 7 ? (
                          <span className="badge badge-amber" style={{ fontSize: 10 }}><Clock size={10} /> {s.days_to_next_inspection}d</span>
                        ) : s.days_to_next_inspection !== null ? (
                          <span className="badge badge-green" style={{ fontSize: 10 }}>{s.days_to_next_inspection}d</span>
                        ) : null}
                      </div>
                    ) : <span className="mono" style={{ color: "#94a3b8", fontSize: 11 }}>—</span>}
                  </td>
                  <td className="mono" style={{ fontSize: 11, color: "#64748b" }}>
                    {s.gps_lat && s.gps_lng ? <><MapPin size={11} style={{ verticalAlign: -1 }} /> {s.gps_lat.toFixed(4)}, {s.gps_lng.toFixed(4)}</> : "—"}
                  </td>
                </tr>
              ))}
              {sorted.length === 0 ? <tr><td colSpan={6} style={{ textAlign: "center", padding: 24, color: "#94a3b8" }}>No active structures.</td></tr> : null}
            </tbody>
          </table>
        </div>
      ) : null}

      {tab === "rams" && (isManagement || ["admin", "contracts_manager"].includes(user?.role)) ? (
        <div className="card" style={{ padding: 0 }} data-testid="mccain-rams-table">
          <table>
            <thead><tr><th>Structure</th><th>Version</th><th>DRA signed by</th><th>DRA signed at</th><th>Comment</th><th>PDF</th></tr></thead>
            <tbody>
              {data.rams.map((r) => (
                <tr key={r.id} data-testid={`mccain-rams-${r.id}`}>
                  <td style={{ fontWeight: 600 }}>{r.structure_description}</td>
                  <td className="mono">v{r.version}</td>
                  <td>{r.dra_sign?.by_name || "—"}</td>
                  <td className="mono" style={{ fontSize: 11 }}>{r.dra_sign?.at ? r.dra_sign.at.slice(0, 19).replace("T", " ") : "—"}</td>
                  <td style={{ fontSize: 12, color: "#475569" }}>{r.dra_sign?.comment || "—"}</td>
                  <td><button data-testid={`mccain-rams-pdf-${r.id}`} className="btn-ghost" style={{ padding: "4px 10px", fontSize: 12 }} onClick={() => downloadRAMSPdf(r.id)}><Download size={12} /> PDF</button></td>
                </tr>
              ))}
              {data.rams.length === 0 ? <tr><td colSpan={6} style={{ textAlign: "center", padding: 24, color: "#94a3b8" }}>No DRA-signed RAMS yet.</td></tr> : null}
            </tbody>
          </table>
        </div>
      ) : null}

      {tab === "handovers" ? (
        <div className="card" style={{ padding: 0 }} data-testid="mccain-handovers-table">
          <table>
            <thead><tr><th>Structure</th><th>Cert ref</th><th>Supervisor</th><th>McCain countersigned</th><th>Countersigned at</th><th>PDF</th></tr></thead>
            <tbody>
              {data.handovers.map((h) => (
                <tr key={h.id} data-testid={`mccain-handover-${h.id}`}>
                  <td style={{ fontWeight: 600 }}>{h.structure_description || h.structure_id}</td>
                  <td className="mono" style={{ fontSize: 11 }}>{h.cert_ref}</td>
                  <td style={{ fontSize: 12 }}>{h.supervisor_name || "—"}</td>
                  <td style={{ fontSize: 12 }}>{h.mccain_name || "—"}</td>
                  <td className="mono" style={{ fontSize: 11 }}>{h.countersigned_at ? h.countersigned_at.slice(0, 19).replace("T", " ") : "—"}</td>
                  <td><button data-testid={`mccain-handover-pdf-${h.id}`} className="btn-ghost" style={{ padding: "4px 10px", fontSize: 12 }} onClick={() => downloadHandoverPdf(h.id)}><Download size={12} /> PDF</button></td>
                </tr>
              ))}
              {data.handovers.length === 0 ? <tr><td colSpan={6} style={{ textAlign: "center", padding: 24, color: "#94a3b8" }}>No countersigned handovers yet.</td></tr> : null}
            </tbody>
          </table>
        </div>
      ) : null}

      {tab === "rota" ? (
        <RotaDiaryView shifts={rotaDiary} />
      ) : null}

      <div className="mono" style={{ fontSize: 10, color: "#94a3b8", marginTop: 24, textAlign: "center" }} data-testid="mccain-generated-at">
        Snapshot generated {data.generated_at?.slice(0, 19).replace("T", " ")} UTC.
      </div>

      {requestModalOpen ? (
        <RequestCalloutModal
          structures={data.structures}
          onClose={() => setRequestModalOpen(false)}
          onSent={(msg) => { setRequestModalOpen(false); setRequestSuccess(msg); }}
        />
      ) : null}
    </McCainShell>
  );
}

function TabBtn({ id, tab, setTab, icon: Icon, label }) {
  const active = tab === id;
  return (
    <button data-testid={`mccain-tab-${id}`} onClick={() => setTab(id)} className="btn-ghost"
      style={{ border: "none", borderRadius: 0, borderBottom: active ? "3px solid var(--brand-accent, #F05A14)" : "3px solid transparent", padding: "10px 14px", fontWeight: 700, background: "transparent", color: active ? "#0f172a" : "#64748b", whiteSpace: "nowrap" }}>
      <Icon size={14} style={{ verticalAlign: -2, marginRight: 6 }} />{label}
    </button>
  );
}

function RotaDiaryView({ shifts }) {
  if (shifts === null) return <div className="card" style={{ padding: 24, textAlign: "center", color: "#64748b" }}>Loading rota…</div>;
  if (shifts.length === 0) return <div className="card" style={{ padding: 24, textAlign: "center", color: "#64748b" }}>No on-call coverage scheduled yet.</div>;
  return (
    <div className="card" data-testid="mccain-rota-diary" style={{ padding: 0 }}>
      <table>
        <thead><tr><th>Date / window</th><th>On call</th><th>Backup</th><th>Paired labourer</th></tr></thead>
        <tbody>
          {shifts.map((s) => (
            <tr key={s.id} data-testid={`mccain-rota-row-${s.id}`}>
              <td style={{ fontWeight: 600 }}>
                <div>{fmtDateTime(s.start_at)}</div>
                <div className="mono" style={{ fontSize: 10, color: "#64748b" }}>→ {fmtDateTime(s.end_at)}</div>
                <div style={{ marginTop: 4 }}>
                  <span className="mono" style={{ background: s.window_type === "night" ? "#0f172a" : "#fef3c7", color: s.window_type === "night" ? "#fff" : "#92400e", padding: "1px 6px", borderRadius: 3, fontSize: 9, fontWeight: 700, letterSpacing: "0.08em" }}>{(s.window_type || "day").toUpperCase()}</span>
                </div>
              </td>
              <td>{s.primary_name || <span style={{ color: "#dc2626" }}>missing</span>}</td>
              <td>{s.backup_name || <span style={{ color: "#94a3b8" }}>—</span>}</td>
              <td>{s.paired_labourer_name || <span style={{ color: "#94a3b8" }}>—</span>}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RequestCalloutModal({ structures, onClose, onSent }) {
  const [description, setDescription] = useState("");
  const [areaOrLine, setAreaOrLine] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const submit = async (e) => {
    e.preventDefault();
    if (!description.trim()) { setError("Description is required so the on-call responder knows what they're walking into."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/mccain/request-callout", {
        callout_description: description.trim(),
        area_or_line: areaOrLine.trim(),
      });
      const sent = data?.nrs_dispatch?.sent;
      onSent(sent ? "Callout submitted — on-call alerted. You'll receive a confirmation when they acknowledge with an ETA." : "Callout submitted — on-call alerted (email delivery pending). Newell will follow up shortly.");
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : JSON.stringify(d));
    } finally { setBusy(false); }
  };

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.55)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16, zIndex: 80 }}>
      <div onClick={(e) => e.stopPropagation()} className="card" data-testid="mccain-callout-modal" style={{ background: "white", maxWidth: 560, width: "100%", padding: 24, maxHeight: "90vh", overflowY: "auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <div>
            <div className="mono" style={{ fontSize: 10, color: "#9a3412", letterSpacing: "0.12em" }}>NEWELL RESPONSE SYSTEM · OUT-OF-HOURS CALLOUT</div>
            <h2 style={{ margin: "4px 0 0", fontWeight: 900, fontSize: 22 }}>Request a callout</h2>
          </div>
          <button className="btn-ghost" onClick={onClose}><X size={16} /></button>
        </div>
        <div style={{ background: "#fff7ed", border: "1px solid #fed7aa", padding: 10, borderRadius: 4, marginBottom: 14, fontSize: 12, color: "#7c2d12" }}>
          The 60-minute response clock starts the moment you submit. You will receive an email confirmation naming the responder + ETA as soon as they acknowledge.
        </div>
        <form onSubmit={submit}>
          <label style={{ display: "block", marginBottom: 10 }}>
            <div className="mono" style={{ fontSize: 11, color: "#64748b", marginBottom: 4 }}>AREA OR LINE</div>
            <input data-testid="mccain-callout-area" value={areaOrLine} onChange={(e) => setAreaOrLine(e.target.value)} placeholder="e.g. Line 5 fryer deck" />
          </label>
          <label style={{ display: "block", marginBottom: 10 }}>
            <div className="mono" style={{ fontSize: 11, color: "#64748b", marginBottom: 4 }}>DESCRIPTION *</div>
            <textarea data-testid="mccain-callout-desc" required value={description} onChange={(e) => setDescription(e.target.value)} rows={5} placeholder="Describe what needs attention" style={{ width: "100%" }} />
          </label>
          {error ? <div data-testid="mccain-callout-error" className="card" style={{ padding: 8, marginBottom: 10, background: "#fef2f2", color: "#b91c1c", borderLeft: "4px solid #dc2626", fontSize: 12 }}><AlertTriangle size={14} style={{ verticalAlign: "middle", marginRight: 6 }} />{error}</div> : null}
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 16 }}>
            <button type="button" className="btn-ghost" onClick={onClose}>Cancel</button>
            <button data-testid="mccain-callout-submit" type="submit" className="btn-primary" disabled={busy} style={{ padding: "12px 18px", fontWeight: 700 }}>
              <Send size={14} style={{ verticalAlign: -2, marginRight: 6 }} />{busy ? "Sending…" : "Submit callout"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
